import { createFileRoute } from '@tanstack/react-router';
import { basicHead } from '../../utility/head';
import ContactForm from '../../components/ContactForm';
import {
  ArrowRightIcon,
  CubeTransparentIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';
import { getButtonStyles } from '@sc/shared-ui';
import EmpowerShape from '../../assets/shapes/empower.svg?react';

const ADURO_URL =
  'https://aduro.io/solutions/stark?utm_source=starkcrawl&utm_medium=contact&utm_campaign=handoff';

const ENQUIRY_TYPES = [
  {
    Icon: CubeTransparentIcon,
    title: 'Bespoke builds on Stark',
    body: 'Stark Crawl is built upon Stark Embedded AI. If you need something the self-serve platform does not cover — white-labelling, per-client billing, or resell terms — Aduro builds it on the same platform.',
  },
  {
    Icon: PuzzlePieceIcon,
    title: 'Custom actions',
    body: 'Aduro professional services can extend Stark Crawl with custom extraction, integrations, or workflow hooks beyond the self-serve product.',
  },
  {
    Icon: ShieldCheckIcon,
    title: 'Compliance built in',
    body: 'Per-client data isolation and UK data residency come with the platform, so regulated work does not need its own compliance build.',
  },
  {
    Icon: WrenchIcon,
    title: 'Technical support',
    body: "Any other technical questions we'll be happy to answer!",
  },
];

export const Route = createFileRoute('/_marketing/contact')({
  component: RouteComponent,
  ssr: true,
  loader: ({ location }) => ({
    pathname: location.pathname,
    seo: {
      title: 'Contact Us - Stark Crawl',
      description:
        'Talk to Aduro, the team behind Stark Crawl, about bespoke builds on Stark Embedded AI, custom integrations, or technical support.',
      keywords:
        'contact Stark Crawl, Aduro, Stark Embedded AI, web crawling enquiry, market research crawl',
    },
  }),
  head: basicHead,
});

function RouteComponent() {
  return (
    <div className="relative overflow-x-hidden">
      <EmpowerShape
        className="absolute left-0 top-20 -translate-x-1/2 w-216 opacity-4 pointer-events-none"
        aria-hidden="true"
      />

      {/* ─── Heading ─────────────────────────────────────────────────────── */}
      <section className="pt-15 lg:pt-20 pb-15 px-5 text-center">
        <div className="max-w-315 mx-auto">
          <h1 className="font-grotesque text-[2.625rem] leading-[1.15] lg:text-[4.375rem] lg:leading-none font-medium text-white">
            Talk to the team who{' '}
            <span className="text-primary-400">built it</span>
          </h1>
          <p className="text-lg leading-6.5 text-grey-300 max-w-180 mx-auto mt-5">
            Stark Crawl is built by Aduro on Stark Embedded AI, our own platform.
            Your message goes straight to that team — the same people who can
            scope a bespoke build, wire in a custom integration, or answer a
            question about how any of it works.
          </p>
        </div>
      </section>

      {/* ─── Form + Sidebar ──────────────────────────────────────────────── */}
      <section className="pb-25 px-5">
        <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-10">
          <div className="bg-background-tertiary backdrop-blur-[25rem] flex flex-col p-6 lg:p-10 lg:w-130 shrink-0 rounded-xs">
            <ContactForm />
          </div>
          <aside
            className="py-8 grow flex flex-col gap-y-10"
            aria-label="Contact information"
          >
            <ul
              className="grow flex flex-col gap-y-10 justify-center"
              role="list"
            >
              {ENQUIRY_TYPES.map(({ Icon, title, body }) => (
                <li key={title} className="flex gap-x-5 items-start">
                  <div
                    className="bg-primary-t-1000 inline-flex items-center justify-center p-3 rounded shrink-0"
                    aria-hidden="true"
                  >
                    <Icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div className="flex flex-col gap-y-2.5">
                    <h4 className="font-grotesque text-[1.875rem] leading-[1.2] font-medium text-white">
                      {title}
                    </h4>
                    <p className="text-base leading-6 text-grey-200">{body}</p>
                  </div>
                </li>
              ))}
            </ul>

            {/* ─── Aduro hand-off ─────────────────────────────────────────── */}
            <div className="bg-background-tertiary rounded-xs px-5 py-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
              <div className="flex items-center gap-x-5">
                <img
                  src="/logos/aduro.svg"
                  alt="Aduro"
                  className="h-10 w-auto shrink-0"
                />
                <p className="text-base leading-6 text-grey-200">
                  Building AI for your own clients? Stark Embedded AI is
                  available direct from Aduro.
                </p>
              </div>
              <a
                href={ADURO_URL}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Talk to Aduro about Stark Embedded AI (opens in new tab)"
                {...getButtonStyles({
                  type: 'low-priority',
                  size: 'large',
                  additionalClassnames: 'gap-x-2 shrink-0',
                })}
              >
                Talk to Aduro
                <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
              </a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
